import {
  type Type,
  Expressions,
  Plugins,
  VariableMap,
  EnvTypes,
  matchExpression,
  Component,
  Variable,
  Types,
  withSystem,
  getSystem,
  withMeta,
  CheckerState,
} from '@packages/systems/wfdl';
import {
  dataProperties,
  getNewFieldLabelDefault,
  getNewFieldDefaultExpression,
} from '@packages/systems/user-components/properties';
import {
  ECall,
  EVariable,
  Expression,
  ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {ExpressionBindMetaPayload} from '@packages/systems/ExpressionEditor-utils';
import {
  identifier,
  identifierToString,
  qualify,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {constantFalse, identity} from '@packages/utilities/fp/utils';
import {generateGuid} from '@packages/utilities/guid';
import {
  EditOverrideButton,
  NewFieldButton,
  UnlinkButton,
  NewConditionalButton,
} from './ExpressionBindingButton';
import {TopLevelOptionsContext, ExpressionBindingHeaderNodeItem} from './types';
import {
  ContextualExpression,
  getAnn,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {
  buildNewConditionalExpression,
  shouldKeepMiniSettingsOpen,
  canCreateConditional,
} from '@packages/systems/plugins-utils/Conditionals';
import {conditionalCreatedAt} from '@packages/systems/conditionals';
import {
  TBoolean,
  toBooleanDefaultValueExpression,
} from '@packages/systems/plugins-utils/Boolean';

export const getBindingTopLevelOptions = (
  element: ExpressionElement,
  componentMap: Component.ComponentMap,
  currentComponentType: Plugins.QualifiedElement,
  dispatch: (arg1: unknown) => void
) => {
  return {
    condition: (
      expr: ContextualExpression,
      setIsEditingField: (arg1: boolean) => void,
      dataType: Type,
      onChange: (
        expression: null | Expression,
        meta: ExpressionBindMetaPayload
      ) => void,
      plugins: Plugins.PluginsType,
      beginNewField: (arg1: {
        newDataType: Type;
        newFieldId: string;
        defaultValue: Expression;
        mapBinding: (arg1: Expression) => Expression;
      }) => void,
      envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>,
      rootExpr: ContextualExpression | null,
      rootCheckerState: CheckerState,
      customTopLevelOptionsContext?: TopLevelOptionsContext
    ) => {
      const staticOptions: Array<ExpressionBindingHeaderNodeItem> = [];
      const secondaryOptions: Array<ExpressionBindingHeaderNodeItem> = [];
      const strippedExpr = stripAll(expr);
      const callExpression = Expressions.getCall(strippedExpr);
      if (
        isSelectWithDataVariable(strippedExpr) ||
        (callExpression && isSelectWithDataVariable(callExpression.arg))
      ) {
        staticOptions.push({
          type: EditOverrideButton,
          dataAutomationId: 'edit-override-field',
          onClick: () => {
            setIsEditingField(true);
          },
        });
      }
      if (Expressions.isBinding(strippedExpr)) {
        staticOptions.push({
          type: UnlinkButton,
          dataAutomationId: 'unlink-override-field',
          onClick: () => {
            const meta: ExpressionBindMetaPayload = {
              analytics: {
                dataType,
                type: 'unbind',
                from: 'node-settings',
              },
            };

            onChange(null, meta);
          },
        });
      }

      if (
        canCreateConditional({
          type: dataType,
          rootExpr,
          expressionPath: getAnn(expr).env.expressionPath,
          plugins,
          checkerState: rootCheckerState,
        })
      ) {
        const expressionPath = getAnn(expr).env.expressionPath;
        secondaryOptions.push({
          type: NewConditionalButton,
          dataAutomationId: 'new-conditional',
          onClick: () => {
            const meta: ExpressionBindMetaPayload = {
              analytics: {
                dataType,
                type: 'new-conditional',
                from: 'node-settings',
              },
            };

            let conditionalExpr: Expression;

            if (Types.isVisibilityConditionsType(dataType)) {
              conditionalExpr = toVisibilityConditionsBinding(
                buildNewConditionalExpression(TBoolean)
              );
            } else {
              conditionalExpr = buildNewConditionalExpression(dataType);
            }

            onChange(conditionalExpr, meta);
            dispatch(
              conditionalCreatedAt({
                expressionPath,
                keepSettingsOpen: shouldKeepMiniSettingsOpen(dataType),
              })
            );
          },
        });
      }

      const getDynamicOptions = (
        variableName: string
      ): Array<ExpressionBindingHeaderNodeItem> => {
        if (identifierToString(variableName) === dataProperties) {
          let bindingLabel = Types.__DEPRECATED__getDisplayName(
            dataType,
            plugins
          );

          if (Types.isVisibilityConditionsType(dataType)) {
            bindingLabel = 'Switch';
          }

          return [
            {
              type: NewFieldButton,
              dataAutomationId: 'add-new-field',
              onClick: () => {
                popoverNewFieldButtonOnClick(
                  element,
                  componentMap,
                  currentComponentType,
                  envTypes,
                  plugins,
                  expr,
                  dataType,
                  beginNewField,
                  customTopLevelOptionsContext ?? {}
                );
              },
              bindingLabel,
            },
          ];
        }
        return [];
      };
      return {
        staticOptions,
        getDynamicOptions,
        secondaryOptions,
      };
    },
  };
};

const isSelectWithDataVariable = matchExpression({
  Select: ({from}) => Expressions.isDataVariable(from),
  _: constantFalse,
});

const popoverNewFieldButtonOnClick = (
  selectedElement: ExpressionElement | null,
  componentMap: Component.ComponentMap,
  currentComponentType: Plugins.QualifiedElement,
  envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>,
  plugins: Plugins.PluginsType,
  expr: ContextualExpression,
  dataType: Type,
  beginNewField: (arg1: {
    newDataType: Type;
    newFieldId: string;
    defaultValue: Expression;
    mapBinding: (arg1: Expression) => Expression;
  }) => void,
  topLevelOptionsContext: TopLevelOptionsContext
) => {
  const source = 'data';
  const newFieldGuid = generateGuid();

  const envDataType = EnvTypes.getType(
    envTypes,
    Variable.unqualified(identifier(source))
  );
  const fields = envDataType ? Types.getRecordFields(envDataType) : null;
  if (!fields) {
    throw new TypeError(
      `ExpressionBinding: Expected envTypes to contain an entry for ${source} to be a "Record", but it was ${
        envDataType ? Types.print(envDataType) : 'missing!'
      }`
    );
  }

  const newFieldDefaultLabel = getNewFieldLabelDefault({
    dataType,
    fields,
    selectedElement,
    currentComponentType,
    componentMap,
    topLevelOptionsContext: {
      ...topLevelOptionsContext,
      nestedPropDataType: getAnn(expr).type ?? undefined,
    },
  });

  const newFieldDefaultExpression = getNewFieldDefaultExpression({
    expression: stripAll(expr),
    plugins,
    dataType,
    envTypes,
    // The binding flow preserves static Attributes items via an Append on the
    // element, so the prop default stays empty rather than duplicating them.
    evaluateAttributes: false,
  });

  if (Types.isVisibilityConditionsType(dataType)) {
    const updatedDataType = withSystem(
      {label: newFieldDefaultLabel},
      withMeta(
        {
          trueLabel: 'Visible',
          falseLabel: 'Hidden',
        },
        TBoolean
      )
    );

    beginNewField({
      newDataType: updatedDataType,
      newFieldId: newFieldGuid,
      defaultValue: toBooleanDefaultValueExpression(newFieldDefaultExpression),
      mapBinding: toVisibilityConditionsBinding,
    });
  } else {
    const updatedDataType = withSystem(
      {
        ...getSystem(dataType),
        label: newFieldDefaultLabel,
      },
      dataType
    );
    beginNewField({
      newDataType: updatedDataType,
      newFieldId: newFieldGuid,
      defaultValue: newFieldDefaultExpression,
      mapBinding: identity,
    });
  }
};

function toVisibilityConditionsBinding(
  booleanSelectExpr: Expression
): Expression {
  return ECall({
    fun: EVariable(
      Variable.qualified(
        qualify(
          Plugins.name('Visibility'),
          typeName('booleanToVisibilityConditions')
        )
      )
    ),
    arg: booleanSelectExpr,
  });
}
